import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useTheme } from '../../context/ThemeContext';
import { useT } from '../../i18n/useT';
import { F, withAlpha } from '../../styles/theme';
import { COIN_MAP } from '../../utils/coingecko';
import { ALL_COINS } from '../../utils/releaseAgenda';
import CoinImage from '../CoinImage';
import { Icon, Icons } from './Icons';
import PickerSheet from '../PickerSheet';

const RED = '#E4544A';

// 2×2-Kachel-Glyph für „Alle Coins" (Überblick) — knüpft an die Marken-DNA
// (2×2-„+"-Zeichen) an, ohne ein Coin-Logo zu sein.
function AllCoinsGlyph({ size = 22, color }) {
  const g = Math.round(size * 0.30);
  const gap = Math.round(size * 0.12);
  return (
    <span aria-hidden style={{
      width: size, height: size, flexShrink: 0,
      display: 'grid', gridTemplateColumns: '1fr 1fr', gridTemplateRows: '1fr 1fr',
      gap, placeContent: 'center',
    }}>
      {[0, 1, 2, 3].map(i => (
        <span key={i} style={{ width: g, height: g, borderRadius: Math.max(2, Math.round(g * 0.28)), background: color, opacity: 0.85 }} />
      ))}
    </span>
  );
}

// iOS-Doppel-Chevron (chevron.up.chevron.down) — die typische "öffnet eine
// Auswahl"-Affordanz aus iOS-Pull-Down-Menüs. Schlank, stroke-basiert.
function IosChevrons({ size = 12, opacity = 0.5 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none"
      stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"
      style={{ opacity, flexShrink: 0 }} aria-hidden>
      <path d="M5 6.5 L8 3.5 L11 6.5" />
      <path d="M5 9.5 L8 12.5 L11 9.5" />
    </svg>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// CoinSelectButton — iOS-Stil-Trigger zum Coin-Wechsel.
//   default : abgerundete Pille, Logo + Ticker (+ Name) + Doppel-Chevron.
//   compact : "nur Logo" — runder Tap-Target mit kleinem Chevron-Badge,
//             für enge Stellen wie das Dashboard-Widget.
// Neutraler Fill (kein roter Tint — Regel feedback_no_pills_borders_glow).
// ──────────────────────────────────────────────────────────────────────────
export function CoinSelectButton({ coin, marketData, onOpen, compact = false, showName = true, size = 26, allLabel = 'Alle Coins' }) {
  const { T } = useTheme();
  const [hover, setHover] = useState(false);
  const isAll = coin === ALL_COINS;

  // „Alle Coins"-Trigger (Überblick) — eigene Pille mit Kachel-Glyph statt Logo.
  if (isAll) {
    return (
      <button
        type="button"
        onClick={onOpen}
        aria-label={allLabel}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 9,
          padding: '5px 11px 5px 9px',
          background: hover ? withAlpha(T.text, 0.10) : withAlpha(T.text, 0.06),
          border: 'none', borderRadius: 999,
          color: T.text,
          fontFamily: F.display, fontSize: 14, fontWeight: 700, letterSpacing: '-0.01em',
          cursor: 'pointer', WebkitTapHighlightColor: 'transparent',
          transition: 'background .15s',
        }}
      >
        <AllCoinsGlyph size={16} color={T.sub} />
        <span>{allLabel}</span>
        <IosChevrons size={13} />
      </button>
    );
  }

  if (compact) {
    // Chevron-Badge skaliert dezent mit der Logo-Größe.
    const badge = Math.max(14, Math.round(size * 0.46));
    return (
      <button
        type="button"
        onClick={onOpen}
        aria-label={coin}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        style={{
          position: 'relative',
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          padding: 3,
          background: hover ? withAlpha(T.text, 0.07) : 'transparent',
          border: 'none', borderRadius: 999,
          cursor: 'pointer', flexShrink: 0,
          WebkitTapHighlightColor: 'transparent',
          transition: 'background .15s',
        }}
      >
        <CoinImage sym={coin} marketData={marketData} size={size} />
        {/* Chevron-Badge unten-rechts signalisiert "antippen zum Wechseln" */}
        <span style={{
          position: 'absolute', right: -1, bottom: -1,
          width: badge, height: badge, borderRadius: 999,
          background: T.surface,
          border: `1px solid ${T.line}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: T.sub,
        }}>
          <svg width={Math.round(badge * 0.55)} height={Math.round(badge * 0.55)} viewBox="0 0 16 16" fill="none"
            stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <path d="M4 6.5 L8 10 L12 6.5" />
          </svg>
        </span>
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label={coin}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 9,
        padding: '5px 11px 5px 5px',
        background: hover ? withAlpha(T.text, 0.10) : withAlpha(T.text, 0.06),
        border: 'none', borderRadius: 999,
        color: T.text,
        fontFamily: F.display, fontSize: 14, fontWeight: 700,
        letterSpacing: '-0.01em',
        cursor: 'pointer',
        WebkitTapHighlightColor: 'transparent',
        transition: 'background .15s',
      }}
    >
      <CoinImage sym={coin} marketData={marketData} size={22} />
      <span style={{ display: 'inline-flex', alignItems: 'baseline', gap: 6 }}>
        <span>{coin}</span>
        {showName && COIN_MAP[coin]?.name && (
          <span style={{ color: T.sub, fontWeight: 500, fontSize: 13 }}>
            {COIN_MAP[coin].name}
          </span>
        )}
      </span>
      <IosChevrons size={13} />
    </button>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// CoinPickerSheet — iOS-Form-Sheet zur Coin-Auswahl. Portal an document.body
// (Glass-Felder mit backdrop-filter sperren sonst position:fixed — siehe
// arch_tx_form_pickers). Mobile: von unten, Desktop: zentriert.
// ──────────────────────────────────────────────────────────────────────────
export function CoinPickerSheet({ coins, current, marketData, onPick, onClose, allOption = false, allLabel = 'Alle Coins' }) {
  const { T } = useTheme();
  const { t } = useT();
  // Items für das zentrale Such-Sheet (Search-Mode, entprellt, ohne Tastatur).
  const items = useMemo(() => {
    const arr = [];
    if (allOption) arr.push({
      key: '__all__', label: allLabel, current: current === ALL_COINS,
      logo: <span style={{ display: 'inline-flex', width: 30, justifyContent: 'center', flexShrink: 0 }}><AllCoinsGlyph size={26} color={T.sub} /></span>,
      onPick: () => onPick(ALL_COINS),
    });
    for (const c of coins) arr.push({
      key: c, label: c, sub: COIN_MAP[c]?.name || '', current: c === current,
      logo: <CoinImage sym={c} marketData={marketData} size={30} />,
      onPick: () => onPick(c),
    });
    return arr;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coins, current, marketData, allOption, allLabel, onPick, T.sub]);

  return (
    <PickerSheet
      open onClose={onClose}
      title={t('tax_choose_coin')}
      searchPlaceholder={t('tax_coin_search_ph')}
      items={items}
      emptyText={t('tax_no_coin_found')}
    />
  );
}
